import { useEffect, useReducer } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIPlugin } from '../../../aiPluginType';

export type State = 'discard' | 'loading' | 'idle' | 'error' | 'prompt';

export type Action =
	| { type: 'discard' }
	| { type: 'cancel' }
	| { type: 'reset' }
	| { type: 'terminate' };

interface UIState {
	discard: boolean;
	terminated: boolean;
}

const initialState: UIState = {
	discard: false,
	terminated: false,
};

function reducer(state: UIState, action: Action): UIState {
	switch (action.type) {
		case 'discard':
			return { ...state, discard: true };
		case 'cancel':
			return { ...state, discard: false };
		case 'terminate':
			return { ...state, terminated: true };
		case 'reset':
			return { discard: false, terminated: false };
		default:
			return state;
	}
}

export const useUIState = (
	api?: ExtractInjectionAPI<AIPlugin>,
): [State, React.Dispatch<Action>] => {
	const [localUI, dispatch] = useReducer(reducer, initialState);

	const pluginState = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => ({
		nextSessionId: state.aiExperienceState?.aiDecorationExperiencePluginState?.nextSessionId,
		status: state.aiExperienceState?.aiSessionPluginState?.status,
	}));

	const { nextSessionId, status } = pluginState || {};

	// RESET LOGIC: Cleans up local state when the editor plugin state clears
	useEffect(() => {
		if (!fg('platform_editor_ai_migrate_from_xstate')) {
			// Legacy Reset: Only cares if the session ID is removed
			if (!nextSessionId) {
				dispatch({ type: 'reset' });
			}
		} else {
			// Migrated Reset: Resets when status is idle AND ID is gone, OR if manually terminated
			if ((!nextSessionId && status === 'idle') || localUI.terminated) {
				dispatch({ type: 'reset' });
			}
		}
	}, [nextSessionId, status, localUI.terminated]);

	// 1. PRIORITY #1: TERMINATION (Global)
	// If the user killed the session, return idle IMMEDIATELY.
	if (localUI.terminated) {
		return ['idle', dispatch];
	}

	// 2. PRIORITY #2: DISCARD (Global)
	if (localUI.discard) {
		return ['discard', dispatch];
	}

	// 3. LEGACY MODE
	if (!fg('platform_editor_ai_migrate_from_xstate')) {
		return nextSessionId ? ['loading', dispatch] : ['idle', dispatch];
	}

	// 4. MIGRATED MODE
	switch (status) {
		case 'prompt':
			return ['prompt', dispatch];
		case 'streaming':
			return ['loading', dispatch];
		case 'error':
			return ['error', dispatch];
		case 'complete':
			return ['idle', dispatch];
		case 'idle':
		default:
			// Transition fallback: show loading if an ID exists but status is pending/idle
			return nextSessionId ? ['loading', dispatch] : ['idle', dispatch];
	}
};
